import React, { FC, useState } from 'react';
import { useHistory } from 'react-router';
import { Formik } from 'formik';
import { useTranslation } from 'react-i18next';
import { random, size } from 'lodash';

import {
  CreateGroupMutation,
  GroupColor,
  GroupContentApprovalType,
  GroupIcon,
  GroupIconColor,
  useCreateGroupMutation,
} from 'api';
import { Layouts } from 'components';
import { useCurrentUser, useNotify } from 'hooks';
import { GroupDataProvider } from 'providers';
import { groupCreate } from 'apollo/reducers';

import { GroupForm } from '../components/FormShared';
import { IGroupForm } from '../types';
import { GroupSchema } from '../validations';

const EditGroup: FC = () => {
  const { t } = useTranslation();
  const currentUser = useCurrentUser();
  const notify = useNotify();
  const history = useHistory();

  const [isWorking, setIsWorking] = useState(false);

  const [createGroupMutation] = useCreateGroupMutation({
    onError: handleError,
    onCompleted: handleCompleted,
  });

  function getInitialValues(): IGroupForm {
    const randomNumberColor = random(0, size(GroupColor) - 1);
    const randomNumberIconColor = random(0, size(GroupIconColor) - 1);
    const randomNumberIcon = random(0, size(GroupIcon) - 1);

    return {
      title: '',
      content_approval_type: GroupContentApprovalType.Auto,
      allow_user_posts: true,
      read_only: false,
      color: Object.values(GroupColor)[randomNumberColor],
      icon_color: Object.values(GroupIconColor)[randomNumberIconColor],
      icon: Object.values(GroupIcon)[randomNumberIcon],
      location_id: null,
      department_id: null,
      is_private: false,
    };
  }

  function handleSubmit(values: IGroupForm) {
    setIsWorking(true);

    return createGroupMutation({
      variables: { data: values },
      update: (cache, result) => {
        groupCreate(cache, result, { currentUser });
      },
    });
  }

  function handleError() {
    setIsWorking(false);
    notify.mutationError();
  }

  function handleCompleted(data: CreateGroupMutation) {
    const { createGroup } = data;

    setIsWorking(false);
    const message = t('groups:createGroupSuccess', { name: createGroup.title });
    notify.info({ message });
    history.push(`/group/${createGroup.id}/edit`);
  }

  function renderMain() {
    return (
      <GroupDataProvider>
        <Formik
          onSubmit={handleSubmit}
          initialValues={getInitialValues()}
          validationSchema={GroupSchema()}>
          <GroupForm action="create" isWorking={isWorking} />
        </Formik>
      </GroupDataProvider>
    );
  }

  return <Layouts.App1Column navigation desktopSize={12} main={renderMain()} />;
};

export default EditGroup;
